import React, { FC, useEffect, useState } from 'react';
import { Redirect, Route, Switch } from '@react-router';
import { Flex, TextV2 } from '@withjoy/joykit';
import { MediaCollectionProps } from './MediaCollection.types';
import { pxToRem } from '@withjoy/joykit/theme';
import { EventDesignPurpose, MediaCollectionEventInfoQuery, useMediaCollectionEventInfoQuery } from '@graphql/generated';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import { MediaCollectionRouter, useRoutePaths } from './MediaCollection.routes';
import { Suspense } from '@shared/core/react/Suspense';
import { MediaCollectionHead } from './MediaCollectionHead';
import { MediaCollectionTelemetryProvider } from './MediaCollection.telemetry';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { display5ToEventTitleOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { EventDesignThemingProvider, useEventDesignThemingProvider } from '@shared/core/website/theming';
import { EventDesignJoyKitThemeOverridesProvider } from '@shared/core/website/theming/EventDesignJoyKitThemeOverridesProvider';

import { LazyAlbumsList } from './routes/AlbumsList/AlbumsList.lazy';
import { LazyViewAlbum } from './routes/ViewAlbum/ViewAlbum.lazy';

const MediaCollectionLoader: FC<{ loadingSite: boolean }> = props => {
  const { loadingSite, children } = props;

  return <> {loadingSite ? <JoyLogoLoader loaderKey="media-collection-guest" /> : children} </>;
};

export const MediaCollection: FC<MediaCollectionProps> = props => {
  const { eventHandle } = props;

  const { data: eventData } = useMediaCollectionEventInfoQuery({
    variables: {
      eventHandle,
      eventDesignPurpose: EventDesignPurpose.live,
    },
    batchMode: 'fast',
    ssr: true,
    fetchPolicy: 'cache-first',
  });

  return (
    <>
      {eventData?.eventByName && (
        <MediaCollectionHead
          eventDisplayName={eventData.eventByName.info.eventDisplayName}
          eventType={eventData.eventByName.info.eventType}
          ownerFirstName={eventData.eventByName.info.ownerFirstName}
          ownerLastName={eventData.eventByName.info.ownerLastName}
          fianceeFirstName={eventData.eventByName.info.fianceeFirstName}
          imageUrl={eventData.eventByName.photo?.url}
          handle={eventHandle}
        />
      )}
      <EventDesignThemingProvider eventDesign={eventData?.eventByName?.eventDesign}>
        <MediaCollectionInner
          eventHandle={eventHandle}
          eventData={eventData}
        />
      </EventDesignThemingProvider>
    </>
  );
};

const MediaCollectionInner: FC<{ eventHandle: string; eventData: MediaCollectionEventInfoQuery | undefined }> = ({ eventHandle, eventData }) => {
  const routePaths = useRoutePaths();
  const { loadingFonts } = useEventDesignThemingProvider();

  const [isInitialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    if (isInitialLoad) {
      setInitialLoad(false);
    }
  }, [isInitialLoad, loadingFonts]);

  return (
    <MediaCollectionLoader loadingSite={loadingFonts || isInitialLoad}>
      <EventDesignJoyKitThemeOverridesProvider>
        <Flex
          width="100%"
          justifyContent="center"
        >
          <Flex
            width="100%"
            flexDirection="column"
            gap={7}
            maxWidth={pxToRem(1280)}
            paddingTop={7}
            paddingBottom={pxToRem(80)}
            paddingX={{ _: 7, sm4: 10 }}
          >
            <Flex
              flexDirection="column"
              width="100%"
              alignItems="center"
              gap={3}
            >
              <GuestSiteTypographyOverride override={display5ToEventTitleOverride}>
                <TextV2
                  typographyVariant="display5"
                  color="mono14"
                  textAlign="center"
                >
                  {eventData?.eventByName?.info.eventDisplayName}
                </TextV2>
              </GuestSiteTypographyOverride>
            </Flex>
            <Switch>
              <Route
                path={routePaths.viewAlbums.path}
                exact={true}
                render={({ match }) => (
                  <Suspense fallback={<></>}>
                    <LazyViewAlbum
                      magicToken={match.params.magicToken}
                      eventHandle={eventHandle}
                      eventId={eventData?.eventByName?.id || ''}
                    />
                  </Suspense>
                )}
              />
              <Route
                path={routePaths.albums.path}
                render={({ match }) => (
                  <Suspense fallback={<></>}>
                    <LazyAlbumsList
                      magicToken={match.params.magicToken}
                      eventHandle={eventHandle}
                    />
                  </Suspense>
                )}
              />
              <Route
                path="*"
                render={() => <Redirect to="/notfound" />}
              />
            </Switch>
          </Flex>
        </Flex>
      </EventDesignJoyKitThemeOverridesProvider>
    </MediaCollectionLoader>
  );
};

export const MediaCollectionProvider: FC<MediaCollectionProps> = props => {
  return (
    <MediaCollectionTelemetryProvider>
      <MediaCollectionRouter eventHandle={props.eventHandle}>
        <MediaCollection {...props} />
      </MediaCollectionRouter>
    </MediaCollectionTelemetryProvider>
  );
};

MediaCollection.displayName = 'MediaCollection';
